import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

// Portfolio Image Component with Error Handling
function PortfolioImage({ src, alt, fallbackGradient }) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Reset error state when src changes
  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setIsLoading(false);
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  if (hasError) {
    // Extract first few words from alt text for display
    const displayText = alt ? alt.split(' ').slice(0, 2).join(' ') : 'Kitchen Design';
    
    return (
      <div className="portfolio-image" style={{
        background: fallbackGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '1.2rem',
        fontWeight: '600',
        textAlign: 'center',
        padding: '1rem'
      }}>
        {displayText}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {isLoading && (
        <div className="portfolio-image" style={{
          background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '0.9rem',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1
        }}>
          Loading...
        </div>
      )}
      <img 
        src={imgSrc} 
        alt={alt}
        className="portfolio-image"
        onError={handleError}
        onLoad={handleLoad}
        style={{ 
          objectFit: 'cover', 
          width: '100%', 
          height: '100%',
          display: 'block',
          position: 'relative',
          zIndex: 2,
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease'
        }}
        loading="lazy"
      />
    </div>
  );
}

function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleDropdownToggle = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleDropdownClose = () => {
    setActiveDropdown(null);
  };

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside dropdown
      if (activeDropdown && !event.target.closest('.nav-dropdown')) {
        setActiveDropdown(null);
      }
      
      // Close mobile menu if clicking outside navbar and menu is open
      if (mobileMenuOpen && !event.target.closest('.navbar')) {
        setMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    // Add both mouse and touch events for better mobile support
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeDropdown, mobileMenuOpen]);

  // Close mobile menu when pressing Escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (mobileMenuOpen) {
          setMobileMenuOpen(false);
          setActiveDropdown(null);
        } else if (activeDropdown) {
          setActiveDropdown(null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen, activeDropdown]);


  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">Seemanchal Decor</div>
          <button 
            className="mobile-menu-toggle" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            <span className={mobileMenuOpen ? 'hamburger open' : 'hamburger'}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`} id="mobile-navigation">
            <li><a href="#home" onClick={closeMobileMenu}>Home</a></li>
            <li><a href="#about" onClick={closeMobileMenu}>About</a></li>
            <li 
              className={`nav-dropdown ${activeDropdown === 'services' ? 'active' : ''}`}
              onMouseLeave={() => window.innerWidth >= 768 && setActiveDropdown(null)}
            >
              <a 
                href="#services" 
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth < 768) {
                    handleDropdownToggle('services');
                  } else {
                    window.location.hash = '#services';
                    closeMobileMenu();
                  }
                }}
                onMouseEnter={() => window.innerWidth >= 768 && setActiveDropdown('services')}
              >
                Services <span className="dropdown-arrow">▼</span>
              </a>
              <ul className={`dropdown-menu ${activeDropdown === 'services' ? 'active' : ''}`}>
                <li><a href="#services" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Kitchen Design & Planning</a></li>
                <li><a href="#services" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Custom Cabinetry</a></li>
                <li><a href="#services" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Premium Materials</a></li>
                <li><a href="#services" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Installation & Renovation</a></li>
                <li><a href="#services" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Lighting Design</a></li>
                <li><a href="#services" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>3D Visualization</a></li>
              </ul>
            </li>
            <li 
              className={`nav-dropdown ${activeDropdown === 'portfolio' ? 'active' : ''}`}
              onMouseLeave={() => window.innerWidth >= 768 && setActiveDropdown(null)}
            >
              <a 
                href="#portfolio" 
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth < 768) {
                    handleDropdownToggle('portfolio');
                  } else {
                    window.location.hash = '#portfolio';
                    closeMobileMenu();
                  }
                }}
                onMouseEnter={() => window.innerWidth >= 768 && setActiveDropdown('portfolio')}
              >
                Design Gallery <span className="dropdown-arrow">▼</span>
              </a>
              <ul className={`dropdown-menu ${activeDropdown === 'portfolio' ? 'active' : ''}`}>
                <li><a href="#portfolio" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Modular Kitchen Designs</a></li>
                <li><a href="#portfolio" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Wardrobe Designs</a></li>
                <li><a href="#portfolio" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Bedroom Designs</a></li>
                <li><a href="#portfolio" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Living Room Designs</a></li>
                <li><a href="#portfolio" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Space Saving Designs</a></li>
                <li><a href="#portfolio" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>All Projects</a></li>
              </ul>
            </li>
            <li><a href="#team" onClick={closeMobileMenu}>Team</a></li>
            <li 
              className={`nav-dropdown ${activeDropdown === 'guides' ? 'active' : ''}`}
              onMouseLeave={() => window.innerWidth >= 768 && setActiveDropdown(null)}
            >
              <a 
                href="#guides" 
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth < 768) {
                    handleDropdownToggle('guides');
                  } else {
                    window.location.hash = '#guides';
                    closeMobileMenu();
                  }
                }}
                onMouseEnter={() => window.innerWidth >= 768 && setActiveDropdown('guides')}
              >
                Guides <span className="dropdown-arrow">▼</span>
              </a>
              <ul className={`dropdown-menu ${activeDropdown === 'guides' ? 'active' : ''}`}>
                <li><a href="#guides" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Kitchen Design Guides</a></li>
                <li><a href="#guides" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Bedroom Design Guides</a></li>
                <li><a href="#guides" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Living Room Guides</a></li>
                <li><a href="#guides" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Materials & Finishes</a></li>
                <li><a href="#guides" onClick={closeMobileMenu} onMouseEnter={() => window.innerWidth >= 768 && handleDropdownClose()}>Cost Calculator</a></li>
              </ul>
            </li>
            <li><a href="#testimonials" onClick={closeMobileMenu}>Testimonials</a></li>
            <li><a href="#contact" onClick={closeMobileMenu}>Contact</a></li>
            <li>
              <Link to="/login" onClick={closeMobileMenu} className="nav-login-link">
                🔐 Login
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`
      }}>
        <div className="hero-content">
          <h1 className="hero-title">Transform Your Kitchen Into a Masterpiece</h1>
          <p className="hero-subtitle">Expert Interior Design Services for Modern, Functional, and Beautiful Kitchens</p>
          <a href="#contact" className="cta-button">Get Free Consultation</a>
        </div>
      </section>

      {/* How It Works Section - DesignCafe Inspired */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <h2 className="section-title">Home Interiors Made Easy</h2>
          <p className="section-subtitle">
            Book a <strong>FREE consultation</strong>, get expert guidance from our <strong>top designers</strong>, 
            and relax as we handle everything <strong>from design to installation</strong>. 
            Your dream home is just a step away. <strong>Let's design your dream home today!</strong>
          </p>
          <div className="how-it-works-grid">
            <div className="step-card">
              <div className="step-number">01</div>
              <h3>Book an appointment</h3>
              <p>Schedule a free consultation with our design experts to discuss your vision and requirements.</p>
            </div>
            <div className="step-card">
              <div className="step-number">02</div>
              <h3>Meet our designers</h3>
              <p>Work with experienced designers who understand your style and create personalized solutions.</p>
            </div>
            <div className="step-card">
              <div className="step-number">03</div>
              <h3>Personalise your designs</h3>
              <p>Review 3D visualizations and customize every detail to match your preferences and lifestyle.</p>
            </div>
            <div className="step-card">
              <div className="step-number">04</div>
              <h3>Move in</h3>
              <p>Relax as we handle installation and delivery. Your dream home is ready for you to move in!</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <a href="#contact" className="cta-button">Get Started Now</a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about" itemScope itemType="https://schema.org/AboutPage">
        <div className="container">
          <h2 className="section-title">About Us</h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                With over a decade of experience in kitchen interior design, we specialize in creating 
                stunning, functional kitchens that reflect your personal style and enhance your daily living. 
                Our team of expert designers combines creativity with practicality to deliver spaces that 
                are both beautiful and efficient.
              </p>
              <p>
                From modern minimalist designs to classic traditional styles, we work closely with you 
                to bring your vision to life. Every project is unique, and we take pride in delivering 
                exceptional results that exceed expectations.
              </p>
            </div>
            <div className="about-stats">
              <div className="stat">
                <h3>100+</h3>
                <p>Projects Completed</p>
              </div>
              <div className="stat">
                <h3>10+</h3>
                <p>Years Experience</p>
              </div>
              <div className="stat">
                <h3>98%</h3>
                <p>Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section id="mission-vision" className="mission-vision">
        <div className="container">
          <h2 className="section-title">Mission & Vision</h2>
          <div className="mission-vision-grid">
            <div className="mission-card">
              <div className="mission-icon">🎯</div>
              <h3 className="mission-title">Our Mission</h3>
              <p className="mission-text">
                To deliver dignified, affordable interior and woodwork solutions to rural and semi-urban homes 
                while empowering local artisans through skill development, livelihood generation, and creative dignity.
              </p>
              <p className="mission-text">
                We aim to bridge the design divide between urban and rural India by creating a decentralized, 
                community-driven interior ecosystem that uplifts both homes and hands.
              </p>
            </div>
            <div className="vision-card">
              <div className="vision-icon">🌟</div>
              <h3 className="vision-title">Our Vision</h3>
              <p className="vision-text">
                To transform the way rural India lives and works by making quality home design a right, not a 
                privilege, and by building a self-reliant network of skilled artisans who shape the interiors 
                of Bharat with pride, purpose, and local wisdom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team">
        <div className="container">
          <h2 className="section-title">People Behind the Idea</h2>
          <p className="section-subtitle">Meet the passionate team dedicated to transforming your kitchen spaces</p>
          <div className="team-grid">
            <div className="team-card">
              <div className="team-avatar">
                <div className="avatar-initials">AM</div>
              </div>
              <h3 className="team-name">Avitesh Mahajan</h3>
              <p className="team-role">Co-founder & Director</p>
              <div className="team-details">
                <div className="team-detail-item">
                  <span className="detail-label">Education:</span>
                  <span className="detail-value">Bachelor's</span>
                </div>
                <div className="team-detail-item">
                  <span className="detail-label">Experience:</span>
                  <span className="detail-value">7+ Years in Rural Interiors & Woodwork</span>
                </div>
              </div>
              <p className="team-description">
                Avitesh brings ground-up experience in designing and delivering affordable interiors tailored for rural and semi-urban homes. With a strong grasp of woodwork and on-site project execution, he leads local teams to create spaces that are practical, aesthetic, and built with local wisdom. His deep connection with the field helps bridge the gap between design intent and grassroots delivery.
              </p>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <div className="avatar-initials">AK</div>
              </div>
              <h3 className="team-name">Aditya Kumar</h3>
              <p className="team-role">Co-founder & Director</p>
              <div className="team-details">
                <div className="team-detail-item">
                  <span className="detail-label">Education:</span>
                  <span className="detail-value">Bachelor's</span>
                </div>
                <div className="team-detail-item">
                  <span className="detail-label">Experience:</span>
                  <span className="detail-value">7+ Years in Rural Interiors & Woodwork</span>
                </div>
              </div>
              <p className="team-description">
                Aditya complements the vision with hands-on expertise in carpentry, site coordination, and rural client engagement. With a focus on affordability, function, and cultural relevance, he has led multiple interior projects across small towns and villages. His strength lies in turning constraints into creativity - using simple materials, skilled hands, and sharp design sense.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services" itemScope itemType="https://schema.org/Service">
        <div className="container">
          <h2 className="section-title">Our Services</h2>
          <div className="services-grid">
            <Link 
              to="/service/kitchen-design-planning"
              className="service-card clickable"
            >
              <div className="service-icon">🏗️</div>
              <h3>Kitchen Design & Planning</h3>
              <p>Complete design solutions from concept to completion, including layout optimization and space planning.</p>
              <span className="service-quote-btn">Get a Quote</span>
            </Link>
            <Link 
              to="/service/custom-cabinetry"
              className="service-card clickable"
            >
              <div className="service-icon">🎨</div>
              <h3>Custom Cabinetry</h3>
              <p>Bespoke cabinetry designed to maximize storage and complement your kitchen's aesthetic.</p>
              <button className="service-quote-btn" onClick={(e) => { e.preventDefault(); window.location.href = '/service/custom-cabinetry'; }}>
                Get a Quote
              </button>
            </Link>
            <Link 
              to="/service/premium-materials"
              className="service-card clickable"
            >
              <div className="service-icon">💎</div>
              <h3>Premium Materials</h3>
              <p>Access to high-quality countertops, backsplashes, and fixtures from trusted suppliers.</p>
              <button className="service-quote-btn" onClick={(e) => { e.preventDefault(); window.location.href = '/service/premium-materials'; }}>
                Get a Quote
              </button>
            </Link>
            <Link 
              to="/service/installation-renovation"
              className="service-card clickable"
            >
              <div className="service-icon">🔧</div>
              <h3>Installation & Renovation</h3>
              <p>Professional installation services ensuring your kitchen is built to last with attention to detail.</p>
              <button className="service-quote-btn" onClick={(e) => { e.preventDefault(); window.location.href = '/service/installation-renovation'; }}>
                Get a Quote
              </button>
            </Link>
            <Link 
              to="/service/lighting-design"
              className="service-card clickable"
            >
              <div className="service-icon">💡</div>
              <h3>Lighting Design</h3>
              <p>Strategic lighting solutions to create ambiance and enhance functionality in your kitchen space.</p>
              <button className="service-quote-btn" onClick={(e) => { e.preventDefault(); window.location.href = '/service/lighting-design'; }}>
                Get a Quote
              </button>
            </Link>
            <Link 
              to="/service/3d-visualization"
              className="service-card clickable"
            >
              <div className="service-icon">🎯</div>
              <h3>3D Visualization</h3>
              <p>See your dream kitchen before it's built with our advanced 3D design and visualization tools.</p>
              <button className="service-quote-btn" onClick={(e) => { e.preventDefault(); window.location.href = '/service/3d-visualization'; }}>
                Get a Quote
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio">
        <div className="container">
          <h2 className="section-title">Our Work</h2>
          <p className="section-subtitle">Explore some of our recent kitchen transformation projects</p>
          <div className="portfolio-grid">
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?w=800&auto=format&fit=crop"
                alt="Seemanchal Decor Special Kitchen"
                fallbackGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              />
              <div className="portfolio-overlay">
                <h3>seemanchal Decor Special</h3>
                <p>Our signature kitchen design</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&auto=format&fit=crop"
                alt="Modern Minimalist Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Modern Minimalist</h3>
                <p>Clean lines and sleek finishes</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912173-6940b9786e3c?w=800&auto=format&fit=crop"
                alt="Classic Elegance Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Classic Elegance</h3>
                <p>Timeless traditional design</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?w=800&auto=format&fit=crop"
                alt="Contemporary Style Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Contemporary Style</h3>
                <p>Bold and innovative</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912167-f556f1f39f4a?w=800&auto=format&fit=crop"
                alt="Rustic Charm Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Rustic Charm</h3>
                <p>Warm and inviting spaces</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556911220-bff31c812dba?w=800&auto=format&fit=crop"
                alt="Luxury Design Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Luxury Design</h3>
                <p>Premium and sophisticated</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912173-2c0b4f4b2c6c?w=800&auto=format&fit=crop"
                alt="Modern Classic Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Modern Classic</h3>
                <p>Elegant and functional</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&auto=format&fit=crop"
                alt="Scandinavian Style Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Scandinavian Style</h3>
                <p>Light, airy, and minimalist</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=800&auto=format&fit=crop"
                alt="Industrial Modern Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Industrial Modern</h3>
                <p>Bold materials and sleek design</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912172-380c23b38d7b?w=800&auto=format&fit=crop"
                alt="Cozy Family Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Cozy Family Kitchen</h3>
                <p>Perfect for family gatherings</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912172-58ae12a0d612?w=800&auto=format&fit=crop"
                alt="Open Concept Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Open Concept</h3>
                <p>Spacious and inviting</p>
              </div>
            </div>
            <div className="portfolio-item">
              <PortfolioImage 
                src="https://images.unsplash.com/photo-1556912173-3814e4a1c8c5?w=800&auto=format&fit=crop"
                alt="Elegant White Kitchen"
              />
              <div className="portfolio-overlay">
                <h3>Elegant White Kitchen</h3>
                <p>Timeless and sophisticated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guides Section - DesignCafe Inspired */}
      <section id="guides" className="guides">
        <div className="container">
          <h2 className="section-title">Design Guides & Resources</h2>
          <p className="section-subtitle">Expert tips and comprehensive guides to help you make informed decisions</p>
          <div className="guides-grid">
            <div className="guide-card">
              <div className="guide-icon">🍳</div>
              <h3>Kitchen Design Guides</h3>
              <p>Complete guide to planning your dream kitchen, from layout to materials selection.</p>
              <a href="#contact" className="guide-link">Read More →</a>
            </div>
            <div className="guide-card">
              <div className="guide-icon">🛏️</div>
              <h3>Bedroom Design Guides</h3>
              <p>Create a peaceful sanctuary with our comprehensive bedroom design tips and ideas.</p>
              <a href="#contact" className="guide-link">Read More →</a>
            </div>
            <div className="guide-card">
              <div className="guide-icon">🛋️</div>
              <h3>Living Room Guides</h3>
              <p>Transform your living space into a welcoming area for family and guests.</p>
              <a href="#contact" className="guide-link">Read More →</a>
            </div>
            <div className="guide-card">
              <div className="guide-icon">💎</div>
              <h3>Materials & Finishes</h3>
              <p>Learn about different materials, their properties, and how to choose the right ones.</p>
              <a href="#contact" className="guide-link">Read More →</a>
            </div>
            <div className="guide-card">
              <div className="guide-icon">💰</div>
              <h3>Cost Calculator</h3>
              <p>Estimate your project cost with our easy-to-use calculator and budget planning tools.</p>
              <a href="#contact" className="guide-link">Calculate Now →</a>
            </div>
            <div className="guide-card">
              <div className="guide-icon">📐</div>
              <h3>Space Optimization</h3>
              <p>Maximize your space with smart design solutions and space-saving techniques.</p>
              <a href="#contact" className="guide-link">Read More →</a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials">
        <div className="container">
          <h2 className="section-title">What Our Clients Say</h2>
          <p className="section-subtitle">Don't just take our word for it - hear from our satisfied customers</p>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                "Seemanchal Decor transformed our kitchen beyond our expectations! The team was professional, 
                creative, and attentive to every detail. Our new kitchen is not just beautiful, it's highly 
                functional too."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">RK</div>
                <div>
                  <h4>Rajesh Kumar</h4>
                  <p>Homeowner</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                "Working with Seemanchal Decor was a dream experience. They understood our vision and 
                brought it to life perfectly. The quality of workmanship is outstanding, and we couldn't 
                be happier with the results!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">PM</div>
                <div>
                  <h4>Priya Mehta</h4>
                  <p>Business Owner</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                "The kitchen design service was exceptional from start to finish. They provided excellent 
                guidance on materials and layout, and the final result exceeded all our expectations. 
                Highly recommended!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">AS</div>
                <div>
                  <h4>Anil Sharma</h4>
                  <p>Architect</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact" itemScope itemType="https://schema.org/ContactPage">
        <div className="container">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">Ready to transform your kitchen? Let's discuss your project</p>
          <div className="contact-content">
            <div className="contact-info" itemScope itemType="https://schema.org/PostalAddress">
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div>
                  <h4>Phone</h4>
                  <p><a href="tel:+917979057848" itemProp="telephone">+91 79790 57848</a></p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div>
                  <h4>Email</h4>
                  <p><a href="mailto:Avitesh2010@gmail.com" itemProp="email">Avitesh2010@gmail.com</a></p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <h4>Address</h4>
                  <p itemProp="streetAddress">C/O Shoba Devi, At Bara Bajar, P.O. Katihar, Ward 25, Bara Bazar</p>
                  <p><span itemProp="addressLocality">Katihar</span>, <span itemProp="addressRegion">Bihar</span> - <span itemProp="postalCode">854105</span></p>
                </div>
              </div>
            </div>
            <form className="contact-form">
              <input type="text" placeholder="Your Name" required />
              <input type="email" placeholder="Your Email" required />
              <input type="tel" placeholder="Phone Number" />
              <textarea placeholder="Tell us about your project" rows="5" required></textarea>
              <button type="submit" className="submit-button">Send Message</button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Seemanchal Decor</h3>
              <p>Creating beautiful kitchens that inspire and delight.</p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#portfolio">Portfolio</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Follow Us</h4>
              <div className="social-links">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">Facebook</a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">Instagram</a>
                <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" aria-label="Pinterest">Pinterest</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Seemanchal Decor. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;

